// Initializing Client-Side RUM Stuff for Client-Side Monitoring
// AwsRumConfig is a TypeScript *type* — aws-rum-web exports no runtime binding
// by that name, and nothing here used it. webpack downgrades a missing named
// export to a warning; native ESM throws a hard SyntaxError that aborts the
// entire entry module, so importing it white-screens the app under Vite.
import { AwsRum } from 'aws-rum-web';
import { getActionHistory, logCrash } from './_actionLogger';
import http from 'helpers/requests';
import { redactSensitiveUrl } from 'helpers/redactSensitiveUrl';

// Store user info that can be updated from components
let currentUserInfo = {
  userId: null,
  email: null,
  firstName: null,
  lastName: null,
  organizationId: null,
};

// Error deduplication tracking
const sentErrorCache = new Map();
const ERROR_COOLDOWN_MS = 5000; // 5 seconds cooldown between similar errors

// Function to update user info (called from components)
export function updateUserInfoForCrashReporting(userInfo) {
  if (userInfo) {
    currentUserInfo = {
      ...currentUserInfo,
      ...userInfo,
    };
  }
}

let awsRum;
let rumInitialized = false;
let rumErrorHandlersInitialized = false;

// Function to make the actual API call
async function sendCrashEmailToServer(data) {
  return http.post('/emails/server-notification', data);
}

// Function to send email notification about crashes
export function sendCrashEmail(error, info) {
  // Skip paths must still IDENTIFY the error — "skipping email" with no
  // detail turned a QA render failure into an undebuggable black box. The
  // raw object is passed last so devtools renders it expandable (stack,
  // own props and all).
  //check if the error doesn't have a stack trace
  if (!info.componentStack) {
    // eslint-disable-next-line no-console
    console.error(
      `[crash-report] email skipped (no React componentStack) — ${error?.name || 'Error'}: ${error?.message || String(error)}`,
      { source: info?.source, stack: error?.stack || '(no stack)' },
      error,
    );
    return false;
  }

  //check if the error is a promise rejection
  if (info.source === 'unhandledrejection') {
    // eslint-disable-next-line no-console
    console.error(
      `[crash-report] email skipped (promise rejection) — ${error?.name || 'Error'}: ${error?.message || String(error)}`,
      { stack: error?.stack || '(no stack)' },
      error,
    );
    return false;
  }

  // Create a unique key for this error
  const errorKey = `${error.message}:${redactSensitiveUrl(window.location.pathname)}`;

  // Check if we've recently sent this error
  const now = Date.now();
  const lastSent = sentErrorCache.get(errorKey);

  if (lastSent && now - lastSent < ERROR_COOLDOWN_MS) {
    console.log('Skipping duplicate error email for:', errorKey);
    return false; // Skip sending - already sent recently
  }

  // Update cache with current timestamp
  sentErrorCache.set(errorKey, now);

  // Clean up old cache entries (older than 1 minute)
  const CACHE_TTL = 60000; // 1 minute
  for (const [key, timestamp] of sentErrorCache.entries()) {
    if (now - timestamp > CACHE_TTL) {
      sentErrorCache.delete(key);
    }
  }

  // Get user action history
  const actionHistory = getActionHistory();

  // Get current user info from clerk if available
  const userInfo = { ...getUserInfo() };

  // Use cached user info (set by components via updateUserInfoForCrashReporting)
  if (currentUserInfo.userId) {
    // Use the cached user info
    userInfo.clerkUser = { ...currentUserInfo };
  }

  // Get environment with fallback
  const environment = process.env.REACT_APP_ENV_MODE || 'DEVELOPMENT';

  // Prepare the data for the API
  const emailData = {
    error: {
      message: error.message,
    },
    url: redactSensitiveUrl(window.location.href),
    time: new Date().toISOString(),
    environment,
    userAgent: navigator.userAgent,
    userInfo: {
      clerkUser: userInfo.clerkUser
        ? {
            id: userInfo.clerkUser.id,
            email: userInfo.clerkUser.email,
            firstName: userInfo.clerkUser.firstName,
            lastName: userInfo.clerkUser.lastName,
            organizationId: userInfo.clerkUser.organizationId,
          }
        : undefined,
    },
    componentStack: info?.componentStack,
    actionHistory: actionHistory.map((action) => ({
      timestamp: action.timestamp,
      type: action.type,
      description: action.description,
    })),
  };

  // Send the email directly using the API function
  sendCrashEmailToServer(emailData).catch((error) => {
    console.error('Failed to send crash email:', error);
  });

  return true;
}

// Helper function to get user information for better error context
function getUserInfo() {
  // You can enhance this to include more user context
  // like user ID, current page, browser info, etc.
  return {
    url: redactSensitiveUrl(window.location.href),
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  };
}

// Function to determine if an error is critical enough to send an email
function isCriticalError(error, info) {
  // Check if error has been explicitly marked as fatal
  if (info && info.isFatalError === true) {
    return true;
  }

  // Check if error occurred in ErrorBoundary (indicating React crashed)
  if (info && info.componentStack) {
    return true;
  }

  // Check if error originated from ErrorPage fallback component
  if (info && info.source === 'ErrorBoundary') {
    return true;
  }

  // Check for specific React error patterns that indicate fatal issues
  const errorStr = error.toString().toLowerCase();
  if (
    errorStr.includes('maximum update depth exceeded') || // Infinite loop
    errorStr.includes('cannot update a component') || // React lifecycle error
    errorStr.includes('invariant violation') || // React internal error
    errorStr.includes('minified react error') // Production React error
  ) {
    return true;
  }

  // Check if source is a global handler (window.onerror or unhandledrejection)
  // as these are typically more serious
  if (info && (info.source === 'window.onerror' || info.source === 'unhandledrejection')) {
    return true;
  }

  // By default, consider it a non-critical error
  return false;
}

// Tag RUM sessions with the active locale so a locale-specific crash shows up
// as such instead of as undifferentiated noise. Guarded: addSessionAttributes
// is not present on every aws-rum-web version, and RUM must never be able to
// break the app.
export function setRumLocale(locale) {
  try {
    if (awsRum && typeof awsRum.addSessionAttributes === 'function') {
      awsRum.addSessionAttributes({ locale });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[rum] could not set locale attribute', error);
  }
}

export function logRumError(error, info) {
  // FIRST, before any normalization: put the ORIGINAL value in the console.
  // Normalization below replaces non-Error values with `new Error(...)`,
  // whose stack points HERE rather than at the throw site — so this line is
  // the only place the true shape of the failure is guaranteed to survive.
  // (index.jsx's window-error handler also preventDefault()s the browser's
  // native output, making this the primary visibility path.)
  // eslint-disable-next-line no-console
  console.error(`[rum] captured (source: ${info?.source || 'unknown'})`, error, info);

  // Normalize error object if it's not an Error instance
  if (!(error instanceof Error)) {
    if (typeof error === 'string') {
      error = new Error(error);
    } else if (typeof error === 'object') {
      error = new Error(JSON.stringify(error));
    } else {
      error = new Error('Unknown error');
    }
  }

  // Record the crash (with stack + React component stack) into the in-app
  // Action History so it's visible in the viewer and survives the reload.
  logCrash(error, info);

  // Log to AWS RUM if available
  if (awsRum) {
    try {
      awsRum.recordError(error);
    } catch (rumError) {
      console.error('Failed to record error in AWS RUM:', rumError);
    }
  }

  // Get environment with fallback
  const environment = process.env.REACT_APP_ENV_MODE || 'DEVELOPMENT';

  // Only send email notifications in production environment AND for critical errors
  if (
    (environment === 'PROD' || environment === 'DEV_WITH_LOGGING' || environment === 'QA') &&
    isCriticalError(error, info)
  ) {
    // Attempt to send email, but don't throw if it fails
    try {
      return sendCrashEmail(error, info);
    } catch (emailError) {
      console.error('Error while sending crash email:', emailError);
    }
  }

  return false;
}

// only turn on localhost logging if you are testing the RUM integration
// as to avoid throwing useless events into our aws-budget.
export function initRum() {
  if (rumInitialized) return;
  rumInitialized = true;
  let myEnvironment = process.env.REACT_APP_ENV_MODE;

  initRumErrorHandlers();

  if (myEnvironment == 'PROD') {
    awsRum = initRumProd();
  } else if (myEnvironment == 'QA') {
    awsRum = initRumQa();
  } else if (myEnvironment == 'DEV_WITH_LOGGING') {
    awsRum = initRumLocalhost();
  }
}

function initRumErrorHandlers() {
  if (rumErrorHandlersInitialized) return;
  rumErrorHandlersInitialized = true;
  initPromiseRejectionHandler();
}

// Add back promise rejection handling, but with better deduplication
function initPromiseRejectionHandler() {
  window.addEventListener('unhandledrejection', (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason) || 'Unhandled Promise rejection');

    logRumError(error, {
      source: 'unhandledrejection',
      isFatalError: true,
    });

    // Don't prevent default browser behavior - let other handlers work too if needed
  });
}

function initRumProd() {
  try {
    const config = {
      sessionSampleRate: 1,
      identityPoolId: 'eu-west-1:1597b274-e5cd-4a55-950e-22fbf94fce0d',
      endpoint: 'https://dataplane.rum.eu-west-1.amazonaws.com',
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    };

    const APPLICATION_ID = '6ee62b39-def1-4807-9d89-d3de5a983b49';
    const APPLICATION_VERSION = '1.0.0';
    const APPLICATION_REGION = 'eu-west-1';

    const awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
    return awsRum;
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
}

function initRumQa() {
  try {
    const config = {
      sessionSampleRate: 1,
      identityPoolId: 'eu-west-1:c409b6cf-18a1-4a08-b37a-814d89973f82',
      endpoint: 'https://dataplane.rum.eu-west-1.amazonaws.com',
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    };

    const APPLICATION_ID = 'ead31296-611f-46f6-877b-599d02c887fc';
    const APPLICATION_VERSION = '1.0.0';
    const APPLICATION_REGION = 'eu-west-1';

    const awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
    return awsRum;
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
}

// currently this is the localhost app monitor configuration
function initRumLocalhost() {
  // This code snippet is generated by AWS
  try {
    const config = {
      sessionSampleRate: 1,
      identityPoolId: 'eu-west-1:c13397a9-82b4-4cb2-bfb0-a97cfd3e2700',
      endpoint: 'https://dataplane.rum.eu-west-1.amazonaws.com',
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    };

    const APPLICATION_ID = '2babfd5e-187c-48aa-b8a2-5f69b83fccd4';
    const APPLICATION_VERSION = '1.0.0';
    const APPLICATION_REGION = 'eu-west-1';

    const awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
    return awsRum;
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }
}
